import React, { useEffect, useState } from 'react';
import { Box, Typography, Paper, Divider, FormControlLabel, Checkbox } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useFormikContext } from 'formik';
import PageHeader from '../../../../components/PageHeader/PageHeader';
import DynamicDropDownlist from '../../../AddNewVehicles/components/DynamicDropDownlist';
import { searchConsignee, searchConsigneeDock } from '../../../../actions/formAction';
import PropTypes from 'prop-types';
import AddConsigneeDialog from './AddConsigneeDialog';


// Helpers
const getByPath = (obj, path) => {
  if (!obj || !path) return undefined;
  const norm = path.replace(/\[(\w+)\]/g, '.$1').replace(/\['([^']+)'\]/g, '.$1');
  return norm.split('.').filter(Boolean).reduce((acc, key) => (acc == null ? undefined : acc[key]), obj);
};


function ConsigneeInfo({ disabled = false, basePath = '', compact = false, title ,fullDetailsView =true ,clientVal  }) {
  const { t } = useTranslation();
  const formik = useFormikContext();
  const { setFieldValue } = formik;
  const consigneePath = basePath ? `${basePath}.consignee` : 'consignee';
  const notifyPartyPath = basePath ? `${basePath}.notifyParty` : 'notifyParty';
  const consigneeDockPath = basePath ? `${basePath}.consigneeDock` : 'consigneeDock';

  const consignee = getByPath(formik.values, consigneePath);
  const notifyParty = getByPath(formik.values, notifyPartyPath);
  const consigneeDock = getByPath(formik.values, consigneeDockPath);

  // After refactor primaryClient is at root; keep backward compatibility if basePath provided


 // const clientVal = getByPath(formik.values, `${basePath}.primaryClient`) || formik.values?.primaryClient;


  //console.log("clientVal", clientVal);



  const gridSx = {
    width: '100%',
    display: 'grid',
    gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
    gap: { xs: 2, md: 4 },
    pt: { xs: 2, md: 3 },
  };


 
  // Safe formatter to avoid rendering objects as React children
  const formatValue = (v) => {
    if (v == null) return '-';
    if (v instanceof Date) return v.toLocaleDateString();
    if (typeof v === 'object') {
      if (typeof v.name === 'string') return v.name;
      if (typeof v.number === 'string' || typeof v.number === 'number') return String(v.number);
      return '-';
    }
    return String(v);
  };


  const [notifyPartySameAsConsignee, setNotifyPartySameAsConsignee] = useState(
    formatValue(consignee) === formatValue(notifyParty) || false
  );

   useEffect(() => {

    if (notifyPartySameAsConsignee) {
      setFieldValue(notifyPartyPath, consignee || null, false);
    }

  }, [consignee, notifyPartySameAsConsignee, setFieldValue, notifyPartyPath]);

  // Sync shipmentItems with consignee, notifyParty, consigneeDock changes (only for root level)
  useEffect(() => {
    if (basePath) return; // Only sync at root level, not for per-container edits

    const containers = formik.values.containers || [];
    let hasUpdates = false;

    const updatedContainers = containers.map((container) => {
      const shipmentItems = container.shipmentItems || [];
      let containerChanged = false;

      const updatedItems = shipmentItems.map((item) => {
        const nextConsignee = consignee ?? null;
        const nextNotifyParty = notifyParty ?? null;
        const nextConsigneeDock = consigneeDock ?? null;

        if (
          item.consignee !== nextConsignee ||
          item.notifyParty !== nextNotifyParty ||
          item.consigneeDock !== nextConsigneeDock
        ) {
          containerChanged = true;
          hasUpdates = true;
          return {
            ...item,
            consignee: nextConsignee,
            notifyParty: nextNotifyParty,
            consigneeDock: nextConsigneeDock,
          };
        }
        return item;
      });

      return containerChanged ? { ...container, shipmentItems: updatedItems } : container;
    });

    if (hasUpdates) {
      setFieldValue('containers', updatedContainers, false);
    }
  }, [consignee, notifyParty, consigneeDock, basePath, formik.values.containers, setFieldValue]);




  const detailsConsignee = [
    { label: t('Phone Number'), value: consignee?.phoneNumber },
    { label: t('Email'), value: consignee?.email },
    { label: t('Address'), value: [consignee?.street, consignee?.city, consignee?.country].filter(Boolean).join(', ') },

  ].filter(d => d.value != null && d.value !== '');


 const detailsNotifyParty = [
    { label: t('Phone Number'), value: notifyParty?.phoneNumber },
    { label: t('Email'), value: notifyParty?.email },
    { label: t('Address'), value: [notifyParty?.street, notifyParty?.city, notifyParty?.country].filter(Boolean).join(', ') },

  ].filter(d => d.value != null && d.value !== '');



  return (
    <Box sx={{ pt: compact ? 0 : 4 }}>
      {!compact && <PageHeader title={title || t('Consignee  & Notify Party')} />}
      {compact && (
        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1, color: '#0B3C5D' }}>
          {title || t('Consignee')}
        </Typography>
      )}

      {/* Selector */}
      <Box
        sx={{

          px: { xs: 0, md: 3 },
          pt: { xs: 2, md: 3 },
        }}
      >


        <Box>


          <FormControlLabel
            label="Notify Party Same as Consignee"
            control={
              <Checkbox
                name="notifyPartySameAsConsignee"
                checked={notifyPartySameAsConsignee}
                onChange={(e) => {
                  const checked = e.target.checked;
                  if (checked) {
                    formik.setFieldValue(notifyPartyPath, consignee || null, false);
                  } else {
                    formik.setFieldValue(notifyPartyPath, null, false);
                  }
                  setNotifyPartySameAsConsignee(checked);
                }}
                sx={{ "& .MuiSvgIcon-root": { fontSize: 28 } }}
              />
            }
            sx={{ alignSelf: "center" }}
          />


        </Box>

        <Box sx={gridSx}>

          <Box sx={{ width: { xs: '100%', sm: '100%', md: 320 } }}>
            <DynamicDropDownlist
              label="Consignee"
              formik={formik}
              fieldName={consigneePath}
              disabled={!clientVal || disabled}
              loadingKey="isLoadingConsigneeData"
              dataKey="consignee"
              fetchAction={searchConsignee}
              errorKey={consigneePath}
              extraQueryParams={{ clientId: clientVal?.id || null }}
              onChange={(val) => formik.setFieldValue(consigneePath, val, false)}
              modalOptionLabel={t('Add Consignee')}
              modalDialog={<AddConsigneeDialog clientId={clientVal?.id || null} />}
            />


          </Box>







          <Box sx={{ width: { xs: '100%', sm: '100%', md: 320 } }}>
            <DynamicDropDownlist
              label="Notify Party"
              formik={formik}
              fieldName={notifyPartyPath}
              disabled={!clientVal || disabled || notifyPartySameAsConsignee}
              loadingKey="isLoadingConsigneeData"
              dataKey="consignee"
              fetchAction={searchConsignee}
              errorKey={notifyPartyPath}
              extraQueryParams={{ clientId: clientVal?.id || null }}
              onChange={(val) => formik.setFieldValue(notifyPartyPath, val, false)}
            />


          </Box>


          <Box sx={{ width: { xs: '100%', sm: '100%', md: 320 } }}>
            <DynamicDropDownlist
              label="Consignee Dock"
              formik={formik}
              fieldName={consigneeDockPath}
              disabled={ disabled}
              loadingKey="isLoadingConsigneeDockData"
              dataKey="consigneeDock"
              fetchAction={searchConsigneeDock}
              errorKey={consigneeDockPath}
              onChange={(val) => formik.setFieldValue(consigneeDockPath, val, false)}
            />


          </Box>
        </Box>





      </Box>

      {fullDetailsView && (
        <Box sx={{ mt: { xs: 2, md: 3 } }}>
          {/* Details consignee - Full View */}
          {consignee && (
            <Box sx={{ px: { xs: 0, md: 3 }, mt: { xs: 2, md: 3 } }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 700, color: '#0B3C5D' }}>
                {t('Consignee Details')}
              </Typography>

              <Paper elevation={0} sx={{ p: { xs: 2, md: 3 }, border: '1px solid #e5eaef', borderRadius: 2, bgcolor: '#fff' }}>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(auto-fill, minmax(200px,1fr))', md: 'repeat(3,1fr)' }, gap: { xs: 1.5, md: 2 } }}>
                  {detailsConsignee.map((d, i) => (
                    <Box key={`${d.label}-${i}`} sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                      <Typography variant="caption" sx={{ color: 'text.secondary', mb: 0.5, whiteSpace: 'nowrap' }}>
                        {d.label}
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#0B3C5D', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={formatValue(d.value)}>
                        {formatValue(d.value)}
                      </Typography>
                    </Box>
                  ))}
                </Box>
                <Divider sx={{ my: { xs: 2, md: 3 } }} />
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 2 }}>
                  <Box>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>{t('Name')}</Typography>
                    <Typography variant="h6" sx={{ m: 0, color: '#0B3C5D' }}>{formatValue(consignee?.name)}</Typography>
                    <Typography variant="caption" sx={{ display: 'block', mt: .5 }}>
                      <strong>{t('Consignee #')}:</strong> {formatValue(consignee?.consigneeNumber) || '-'}
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            </Box>
          )}

          {/* Details notifyParty - Full View */}
          {notifyParty && (
            <Box sx={{ px: { xs: 0, md: 3 }, mt: { xs: 2, md: 3 } }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 700, color: '#0B3C5D' }}>
                {t('Notify Party Details')}
              </Typography>

              <Paper elevation={0} sx={{ p: { xs: 2, md: 3 }, border: '1px solid #e5eaef', borderRadius: 2, bgcolor: '#fff' }}>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(auto-fill, minmax(200px,1fr))', md: 'repeat(3,1fr)' }, gap: { xs: 1.5, md: 2 } }}>
                  {detailsNotifyParty.map((d, i) => (
                    <Box key={`${d.label}-${i}`} sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                      <Typography variant="caption" sx={{ color: 'text.secondary', mb: 0.5, whiteSpace: 'nowrap' }}>
                        {d.label}
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#0B3C5D', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={formatValue(d.value)}>
                        {formatValue(d.value)}
                      </Typography>
                    </Box>
                  ))}
                </Box>
                <Divider sx={{ my: { xs: 2, md: 3 } }} />
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 2 }}>
                  <Box>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>{t('Name')}</Typography>
                    <Typography variant="h6" sx={{ m: 0, color: '#0B3C5D' }}>{formatValue(notifyParty?.name)}</Typography>
                    <Typography variant="caption" sx={{ display: 'block', mt: .5 }}>
                      <strong>{t('Consignee #')}:</strong> {formatValue(notifyParty?.consigneeNumber) || '-'}
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            </Box>
          )}
        </Box>
      )}

      {/* Compact View - Details in single row when fullDetailsView === false */}
      {!fullDetailsView && (consignee || notifyParty) && (
        <Box sx={{ mt: { xs: 1, md: 1.5 }, px: { xs: 0, md: 3 }, py: 1 }}>
          <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', fontSize: '0.8rem', alignItems: 'flex-start' }}>
            {consignee && (
              <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                <Box>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
                    {t('Consignee')}
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#0B3C5D', fontSize: '0.8rem' }}>
                    {formatValue(consignee?.name)}
                  </Typography>
                </Box>
                {consignee?.phoneNumber && (
                  <Box>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
                      {t('Phone')}
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#0B3C5D', fontSize: '0.8rem' }}>
                      {formatValue(consignee?.phoneNumber)}
                    </Typography>
                  </Box>
                )}
                {consignee?.email && (
                  <Box>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
                      {t('Email')}
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#0B3C5D', fontSize: '0.8rem' }}>
                      {formatValue(consignee?.email)}
                    </Typography>
                  </Box>
                )}
              </Box>
            )}

            {notifyParty && (
              <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                <Box>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
                    {t('Notify Party')}
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#0B3C5D', fontSize: '0.8rem' }}>
                    {formatValue(notifyParty?.name)}
                  </Typography>
                </Box>
                {notifyParty?.phoneNumber && (
                  <Box>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
                      {t('Phone')}
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#0B3C5D', fontSize: '0.8rem' }}>
                      {formatValue(notifyParty?.phoneNumber)}
                    </Typography>
                  </Box>
                )}
                {notifyParty?.email && (
                  <Box>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
                      {t('Email')}
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#0B3C5D', fontSize: '0.8rem' }}>
                      {formatValue(notifyParty?.email)}
                    </Typography>
                  </Box>
                )}
              </Box>
            )}
          </Box>
        </Box>
      )}
      
    </Box>
  );
}
ConsigneeInfo.propTypes = {
  disabled: PropTypes.bool,
  basePath: PropTypes.string,
  compact: PropTypes.bool,
  title: PropTypes.string,
  fullDetailsView: PropTypes.bool,
  clientVal: PropTypes.object,
};

export default ConsigneeInfo;
